/**
 * Observações fiscais do cliente (public.fiscal_cliente_observacao): o que o consultor sabe sobre o cliente e que
 * explica um achado do confronto (regime especial, benefício, particularidade da operação).
 *
 * Cada observação tem um ALCANCE (regras, CFOP, CST do ICMS, NCM, UF de destino, tipo da nota, cenário, vigência) e um
 * EFEITO:
 *   informativa — aparece junto do achado, não muda a contagem;
 *   justifica   — o achado continua na lista, marcado como justificado, e sai da contagem de erros/alertas.
 * Critério vazio não restringe. Para justificar, a observação precisa citar pelo menos uma regra (não existe
 * "justifica tudo do cliente").
 */
import type { Achado, ItemLido, NotaLida } from './tipos';

export type EfeitoObservacao = 'informativa' | 'justifica';

export type ObservacaoFiscal = {
  id: string;
  /** 'cliente' = fiscal_cliente_observacao; 'regime' = efeito de um regime especial do cliente (fiscal_regime_especial_efeito). */
  origem: 'cliente' | 'regime';
  titulo: string;
  efeito: EfeitoObservacao;
  vigenciaInicio: string | null;
  vigenciaFim: string | null;
  regras: string[];
  cfops: string[];
  cstsIcms: string[];
  ncmPrefixos: string[];
  ufsDestino: string[];
  tpNF: string | null;
  cenarioChave: string | null;
};

/** O que se sabe do achado para casar com o alcance. Campo null = desconhecido (achado de nota não tem CFOP/CST/NCM). */
export type ContextoAchado = {
  regra: string;
  cfop: string | null;
  cstIcms: string | null;
  ncm: string | null;
  ufDestino: string | null;
  tpNF: string | null;
  cenarioChave: string | null;
  /** yyyy-mm-dd da emissão; null = não conferir a vigência (tela, que só tem o cenário). */
  data: string | null;
};

const digitos = (s: string) => s.replace(/\D/g, '');
const dentro = (lista: string[], v: string | null) => lista.length === 0 || (v !== null && lista.includes(v));

export function observacaoAlcanca(o: ObservacaoFiscal, c: ContextoAchado): boolean {
  if (!dentro(o.regras, c.regra)) return false;
  if (!dentro(o.cfops, c.cfop)) return false;
  if (!dentro(o.cstsIcms, c.cstIcms)) return false;
  if (!dentro(o.ufsDestino, c.ufDestino)) return false;
  if (o.tpNF !== null && o.tpNF !== c.tpNF) return false;
  if (o.cenarioChave !== null && o.cenarioChave !== c.cenarioChave) return false;
  if (o.ncmPrefixos.length > 0 && !(c.ncm !== null && o.ncmPrefixos.some((p) => digitos(p) !== '' && c.ncm!.startsWith(digitos(p))))) return false;
  if (c.data !== null && ((o.vigenciaInicio !== null && c.data < o.vigenciaInicio) || (o.vigenciaFim !== null && c.data > o.vigenciaFim))) return false;
  return true;
}

/** Só justifica quem cita a regra: observação "justifica" sem regra (dado antigo ou gravado por fora) vale como informativa. */
const justifica = (o: ObservacaoFiscal) => o.efeito === 'justifica' && o.regras.length > 0;

/** A observação que vale para o achado: a primeira que justifica; senão, a primeira informativa. */
export function observacaoDoAchado(observacoes: ObservacaoFiscal[], c: ContextoAchado): ObservacaoFiscal | null {
  let informativa: ObservacaoFiscal | null = null;
  for (const o of observacoes) {
    if (!observacaoAlcanca(o, c)) continue;
    if (justifica(o)) return o;
    informativa ??= o;
  }
  return informativa;
}

export function contextoDoItem(regra: string, nota: NotaLida, item: ItemLido | null, cenarioChave: string | null): ContextoAchado {
  return {
    regra,
    cfop: item?.cfop ?? null,
    cstIcms: item?.icms?.cst ?? null,
    ncm: item?.ncm ?? null,
    ufDestino: nota.dest.uf,
    tpNF: nota.tpNF,
    cenarioChave,
    data: nota.dataEmissao,
  };
}

/**
 * Contexto a partir da chave do cenário (index.ts, chaveCenario: "SC>PR · CRT 3 · tpNF 1 · … · CFOP 5102 · … · ICMS51/51 · …"),
 * para a tela contar o alcance de uma observação num confronto já avaliado. Sem data: a vigência não é conferida.
 */
export function contextoDaChave(regra: string, cenarioChave: string, ncm: string | null, deNota = false): ContextoAchado {
  const partes = cenarioChave.split(' · ');
  const parte = (prefixo: string) => partes.find((p) => p.startsWith(prefixo))?.slice(prefixo.length) ?? null;
  const limpo = (v: string | null | undefined) => (v === null || v === undefined || v === '—' ? null : v);
  const icms = partes.find((p) => /^ICMS[^ ]*\//.test(p));
  return {
    regra,
    cfop: deNota ? null : limpo(parte('CFOP ')),
    cstIcms: deNota ? null : limpo(icms?.split('/')[1]),
    ncm: deNota ? null : ncm,
    ufDestino: limpo(partes[0]?.split('>')[1]),
    tpNF: limpo(parte('tpNF ')),
    cenarioChave: deNota ? null : cenarioChave,
    data: null,
  };
}

/** Marca os achados alcançados por alguma observação (não remove nenhum). */
export function aplicarObservacoes(achados: Achado[], observacoes: ObservacaoFiscal[], contexto: (regra: string) => ContextoAchado): Achado[] {
  if (observacoes.length === 0) return achados;
  return achados.map((a) => {
    const o = observacaoDoAchado(observacoes, contexto(a.regra));
    if (!o) return a;
    return o.origem === 'regime' ? { ...a, regimeEfeitoId: o.id, justificado: justifica(o) } : { ...a, observacaoId: o.id, justificado: justifica(o) };
  });
}

type Linha = Record<string, unknown>;
const lista = (v: unknown) => (Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean) : []);
const texto = (v: unknown) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim());

/** Linha de fiscal_cliente_observacao → observação do motor. */
export function observacaoDaLinha(r: Linha): ObservacaoFiscal {
  return {
    id: String(r.id),
    origem: 'cliente',
    titulo: String(r.titulo ?? ''),
    efeito: r.efeito === 'justifica' ? 'justifica' : 'informativa',
    vigenciaInicio: texto(r.vigencia_inicio),
    vigenciaFim: texto(r.vigencia_fim),
    regras: lista(r.regras),
    cfops: lista(r.cfops),
    cstsIcms: lista(r.csts_icms),
    ncmPrefixos: lista(r.ncm_prefixos),
    ufsDestino: lista(r.ufs_destino).map((u) => u.toUpperCase()),
    tpNF: texto(r.tp_nf),
    cenarioChave: texto(r.cenario_chave),
  };
}

/**
 * Efeito de regime especial → observação do motor. A vigência é a da concessão do cliente (fiscal_cliente_regime),
 * limitada pela do próprio regime; o título leva o código do regime ("TTD 409 — …").
 */
export function observacaoDoRegime(efeito: Linha, regime: Linha, vinculo: Linha): ObservacaoFiscal {
  const maior = (a: string | null, b: string | null) => (a === null ? b : b === null ? a : a > b ? a : b);
  const menor = (a: string | null, b: string | null) => (a === null ? b : b === null ? a : a < b ? a : b);
  return {
    ...observacaoDaLinha({ ...efeito, cenario_chave: null }),
    origem: 'regime',
    titulo: `${String(regime.codigo ?? '')} — ${String(efeito.titulo ?? '')}`,
    vigenciaInicio: maior(texto(vinculo.vigencia_inicio), texto(regime.vigencia_inicio)),
    vigenciaFim: menor(texto(vinculo.vigencia_fim), texto(regime.vigencia_fim)),
  };
}

export const COLUNAS_OBSERVACAO = 'id,titulo,efeito,vigencia_inicio,vigencia_fim,regras,cfops,csts_icms,ncm_prefixos,ufs_destino,tp_nf,cenario_chave';
